import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AccessRights from "../../../../configs/constants/accessRights";
import {
  getTicket,
  getAgents,
  acknowledgeTicket,
  assignTicket,
  escalateTicket,
  updateTicketStatus,
  updateTicketPriority,
  addComment,
  sendAdminComment,
  uploadAttachment,
} from "../../redux/action";
import { clearSelectedTicket } from "../../redux/slice";
import TicketDetailPage from "./ticketDetail.page";

export default function TicketDetailContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const accessRights = useSelector((state) => state.session.accessRights);
  const currentUserSub = useSelector((state) => state.session.idTokenPayload?.sub || "");
  // Real admin name so the optimistic avatar matches the agent's
  // initial instead of the placeholder "Y" from "You".
  const currentUserName = useSelector(
    (state) => state.session.idTokenPayload?.name || "You"
  );

  const hasAccess = accessRights.includes(AccessRights.SUPPORT_TICKET_ACCESS);
  const hasManageAll = accessRights.includes(AccessRights.SUPPORT_TICKET_MANAGE_ALL);
  const hasAckRight = accessRights.includes(AccessRights.SUPPORT_TICKET_ACKNOWLEDGE);
  const hasAssignRight = accessRights.includes(AccessRights.SUPPORT_TICKET_ASSIGN_AGENT);
  const hasEscalateRight = accessRights.includes(AccessRights.SUPPORT_TICKET_ESCALATE);
  const hasStatusRight = accessRights.includes(AccessRights.SUPPORT_TICKET_CHANGE_STATUS);
  const hasPriorityRight = accessRights.includes(AccessRights.SUPPORT_TICKET_SET_PRIORITY);

  useEffect(() => {
    if (!hasAccess) {
      navigate("/admin");
    }
  }, [hasAccess]);

  const ticket = useSelector((state) => state.support.selectedTicket);
  const agents = useSelector((state) => state.support.agents);

  // Ownership + scope derived for current ticket. Open tickets (no assignee) only
  // reachable via MANAGE_ALL since there's no "owner" yet.
  const isOwn = !!(ticket && ticket.assignee_id && ticket.assignee_id === currentUserSub);
  const ownOrAll = hasManageAll || isOwn;

  const canReply = ownOrAll;
  const canAcknowledge = hasAckRight;
  const canAssignAgent = hasAssignRight && ownOrAll;
  const canEscalate = hasEscalateRight && ownOrAll;
  const canChangeStatus = hasStatusRight && ownOrAll;
  const canSetPriority = hasPriorityRight && ownOrAll;
  const isCommentLoading = useSelector(
    (state) =>
      state.loading.inProcess.filter((p) => p.id === addComment.typePrefix)
        .length > 0
  );

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showAcknowledgeDialog, setShowAcknowledgeDialog] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getTicket({ id }));
      dispatch(getAgents());
    }
    return () => {
      dispatch(clearSelectedTicket());
    };
  }, [dispatch, id]);

  const handleBack = () => {
    navigate("/admin/support");
  };

  const handleStatusChange = (status) => {
    dispatch(updateTicketStatus({ id, status })).then(() => {
      dispatch(getTicket({ id }));
    });
  };

  const handlePriorityChange = (priority) => {
    dispatch(updateTicketPriority({ id, priority })).then(() => {
      dispatch(getTicket({ id }));
    });
  };

  const handleAssignClick = () => {
    setShowAssignDialog(true);
  };

  const handleAssign = (agent) => {
    dispatch(
      assignTicket({
        id,
        assigneeId: agent.id,
        assigneeName: agent.name,
      })
    ).then(() => {
      setShowAssignDialog(false);
      dispatch(getTicket({ id }));
    });
  };

  const handleAcknowledgeClick = () => {
    setShowAcknowledgeDialog(true);
  };

  const handleAcknowledge = (priority, agent) => {
    dispatch(
      acknowledgeTicket({
        id,
        priority,
        assigneeId: agent.id,
        assigneeName: agent.name,
      })
    ).then(() => {
      setShowAcknowledgeDialog(false);
      dispatch(getTicket({ id }));
    });
  };

  const handleEscalateClick = () => {
    setShowEscalateDialog(true);
  };

  const handleEscalate = (agent, reason) => {
    dispatch(
      escalateTicket({
        ticketId: id,
        escalatedToAgentId: agent.id,
        escalatedToAgentName: agent.name,
        reason,
      })
    ).then(() => {
      setShowEscalateDialog(false);
      dispatch(getTicket({ id }));
    });
  };

  // Optimistic agent reply (WhatsApp-style). The thunk pushes a synthetic
  // bubble immediately, then races the API against a 10s timeout. The
  // bubble shows clock -> tick on success, or a retry icon on failure.
  const sendOptimistic = async ({ content, isInternal, files, tempId }) => {
    let commentId = null;
    if (content) {
      const result = await dispatch(
        sendAdminComment({
          ticketId: id,
          content,
          isInternal,
          tempId,
          userName: currentUserName,
        })
      );
      if (!result?.ok) {
        // Failed bubble already shown; do NOT proceed with attachments
        // because there is no server comment id to attach them to.
        return result;
      }
      commentId = result.comment?.id;
    }
    if (files && files.length > 0) {
      await Promise.all(
        files.map((file) =>
          dispatch(uploadAttachment({ ticketId: id, file, commentId }))
        )
      );
      dispatch(getTicket({ id }));
    }
    return { ok: true, commentId };
  };

  const handleCommentSubmit = (payload) => sendOptimistic(payload);

  // Re-uses the same tempId so the failed bubble flips back to "sending"
  // in place rather than appending a duplicate at the bottom.
  const handleRetryComment = (failedComment) => {
    if (!failedComment) return undefined;
    return sendOptimistic({
      content: failedComment.content,
      isInternal: !!failedComment.is_internal,
      files: [],
      tempId: failedComment.id,
    });
  };

  return (
    <TicketDetailPage
      ticket={ticket}
      agents={agents}
      isCommentLoading={isCommentLoading}
      showAssignDialog={showAssignDialog}
      showAcknowledgeDialog={showAcknowledgeDialog}
      showEscalateDialog={showEscalateDialog}
      canReply={canReply}
      canAcknowledge={canAcknowledge}
      canAssignAgent={canAssignAgent}
      canEscalate={canEscalate}
      canChangeStatus={canChangeStatus}
      canSetPriority={canSetPriority}
      onBack={handleBack}
      onStatusChange={handleStatusChange}
      onPriorityChange={handlePriorityChange}
      onAssignClick={handleAssignClick}
      onAssign={handleAssign}
      onAssignDialogClose={() => setShowAssignDialog(false)}
      onAcknowledgeClick={handleAcknowledgeClick}
      onAcknowledge={handleAcknowledge}
      onAcknowledgeDialogClose={() => setShowAcknowledgeDialog(false)}
      onEscalateClick={handleEscalateClick}
      onEscalate={handleEscalate}
      onEscalateDialogClose={() => setShowEscalateDialog(false)}
      onCommentSubmit={handleCommentSubmit}
      onRetryComment={handleRetryComment}
    />
  );
}
